import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workshopsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Building2, Plus, Search, Edit, ToggleLeft, ToggleRight, BarChart3, Loader2, Trash2, RefreshCw, History } from 'lucide-react';

const REGIONS = [
  'Dar es Salaam', 'Morogoro', 'Dodoma', 'Mtwara', 'Arusha', 'Mwanza', 
  'Tanga', 'Mbeya', 'Iringa', 'Kilimanjaro', 'Other'
];

export default function WorkshopsPage() {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    region: '',
    license_number: ''
  });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, workshop: null });
  const [deleting, setDeleting] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canManage = ['admin', 'regulator'].includes(user.role);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { include_stats: true };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      
      // Fetch workshops and stats independently — stats failure should NOT block the list
      const workshopsRes = await workshopsAPI.getAll(params);
      setWorkshops(workshopsRes.data);
      
      if (canManage) {
        try {
          const statsRes = await workshopsAPI.getStats();
          if (statsRes.data) setStats(statsRes.data);
        } catch {
          // Stats failed silently — workshops list still works
        }
      }
    } catch (err) {
      toast.error('Failed to load workshops');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadData();
  };

  const openCreateDialog = () => {
    setEditingWorkshop(null);
    setFormData({ name: '', address: '', phone: '', email: '', region: '', license_number: '' });
    setShowDialog(true);
  };

  const openEditDialog = (workshop) => {
    setEditingWorkshop(workshop);
    setFormData({
      name: workshop.name || '',
      address: workshop.address || '',
      phone: workshop.phone || '',
      email: workshop.email || '',
      region: workshop.region || '',
      license_number: workshop.license_number || ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Workshop name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingWorkshop) {
        await workshopsAPI.update(editingWorkshop.id, formData);
        toast.success('Workshop updated');
      } else {
        await workshopsAPI.create(formData);
        toast.success('Workshop created');
      }
      setShowDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save workshop');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (workshop) => {
    try {
      if (workshop.status === 'active') {
        await workshopsAPI.deactivate(workshop.id);
        toast.success('Workshop deactivated');
      } else {
        await workshopsAPI.activate(workshop.id);
        toast.success('Workshop activated');
      }
      loadData();
    } catch (err) {
      toast.error('Failed to update workshop status');
    }
  };

  const handleSyncFromHistory = async () => {
    setSyncing(true);
    try {
      const res = await workshopsAPI.syncFromHistory();
      if (res.data.created_workshops?.length > 0) {
        toast.success(`Synced ${res.data.created_workshops.length} workshops from historical data`);
      } else {
        toast.info('No new workshops found in historical data');
      }
      loadData();
    } catch (err) {
      toast.error('Failed to sync workshops');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.workshop) return;
    
    setDeleting(true);
    try {
      await workshopsAPI.delete(deleteDialog.workshop.id);
      toast.success('Workshop deleted');
      setDeleteDialog({ open: false, workshop: null });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete workshop');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="workshops-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            Conversion Workshops
          </h1>
          <p className="text-muted-foreground">
            Manage registered CNG conversion workshops
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSyncFromHistory} disabled={syncing} data-testid="sync-workshops-btn">
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <History className="h-4 w-4 mr-2" />}
              Sync from History
            </Button>
            <Button onClick={openCreateDialog} data-testid="add-workshop-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Workshop
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && canManage && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Workshops</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active_workshops}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Conversions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_conversions}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Top Workshop</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold truncate">
                {stats.workshops?.[0]?.workshop_name || 'N/A'}
              </div>
              <p className="text-sm text-muted-foreground">
                {stats.workshops?.[0]?.conversion_count || 0} conversions
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search workshops..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="workshop-search"
                />
              </div>
            </div>
            {canManage && (
              <div className="w-40">
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button type="submit">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Workshops Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : workshops.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No workshops found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>License #</TableHead>
                  <TableHead className="text-center">Conversions</TableHead>
                  <TableHead>Status</TableHead>
                  {canManage && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {workshops.map((workshop) => (
                  <TableRow key={workshop.id} data-testid={`workshop-row-${workshop.id}`}>
                    <TableCell className="font-medium">{workshop.name}</TableCell>
                    <TableCell>{workshop.region || '-'}</TableCell>
                    <TableCell>{workshop.phone || '-'}</TableCell>
                    <TableCell>{workshop.license_number || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{workshop.conversion_count || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={workshop.status === 'active' ? 'default' : 'secondary'}>
                        {workshop.status}
                      </Badge>
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditDialog(workshop)}
                            title="Edit"
                            data-testid={`edit-workshop-${workshop.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(workshop)}
                            title={workshop.status === 'active' ? 'Deactivate' : 'Activate'}
                            data-testid={`toggle-workshop-${workshop.id}`}
                          >
                            {workshop.status === 'active' ? (
                              <ToggleRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-gray-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, workshop })}
                            title="Delete"
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-workshop-${workshop.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingWorkshop ? 'Edit Workshop' : 'Add Workshop'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Workshop Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., CNG Tanzania"
                data-testid="workshop-name-input"
              />
            </div>
            <div>
              <Label>Region</Label>
              <Select 
                value={formData.region} 
                onValueChange={(v) => setFormData({ ...formData, region: v })}
              >
                <SelectTrigger data-testid="workshop-region-select">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Physical address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+255..."
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  type="email"
                />
              </div>
            </div>
            <div>
              <Label>License Number</Label>
              <Input
                value={formData.license_number}
                onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                placeholder="Business license number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-workshop-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingWorkshop ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, workshop: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workshop</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.workshop?.name}"? 
              This action cannot be undone.
              {deleteDialog.workshop?.conversion_count > 0 && (
                <span className="block mt-2 text-amber-600">
                  Note: Workshops with linked inspections cannot be deleted. Deactivate instead.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
